import { createSlice } from '@reduxjs/toolkit';
import { copilotV1ThreadMessagesForV2 } from '../../threads/actions/copilotV1ThreadMessagesForV2';
import { copilotV2GetLatestOrCreateThread } from '../../threads/actions/copilotV2GetLatestOrCreateThread';
import { copilotV2GetThreadWithMessages } from '../../threads/actions/copilotV2GetThreadWithMessages';
import { copilotV2SoftDeleteAndGetLatestOrCreateNew } from '../../threads/actions/copilotV2SoftDeleteAndGetLatestOrCreateNew';
import { doesSectionSupportStreaming } from '../../types/CopilotV2Types';
import { getShouldSendCsatSurvey } from '../actions/getShouldSendCsatSurvey';
import { initializeAblyChannel } from '../actions/initializeAblyChannel';
import { submitCsatSurvey } from '../actions/submitCsatSurvey';
import { PLACEHOLDER_INTERACTION_ID_USER_MESSAGE } from '../constants/Interactions';
import { getCopilotV2ToolStartText } from '../utils/copilotV2ResponseUtil';
import { createActionExceptionMessage, createCopilotMessage, createErrorMessage, createSystemMessage, createUserMessage } from '../utils/createV2Message';
const initialState = {
  threadId: undefined,
  isCurrentThreadAutocreated: undefined,
  isCreatingThread: false,
  messages: [],
  draftChatMessage: '',
  fetchStatus: 'idle',
  isWaitingForResponse: false,
  isInteractiveModeActive: true,
  hasMore: false,
  offset: undefined,
  pagingStatus: 'idle',
  ablyChannelId: null,
  ablyChannelConnectingState: 'initial-bootup-initialized',
  streaming: {
    currentToolCall: '',
    interactionId: ''
  },
  showCsatSurvey: false,
  shouldPollCsatSurvey: true,
  isSavedPrompt: false,
  isReadOnly: false,
  failedUserMessages: [],
  interactionDurations: {},
  appSearchScope: {
    type: 'SELECTED_APPS',
    includedApps: []
  },
  appsControl: {
    connectedApps: []
  }
};

// TODO: Do we want chatDisconnectPending, chatDisconnectSuccess, and other ably things to be tracked under a separate slice specific to Ably?

// TODO: Implement v2 addLoadingMessageToThread action

const chatV2Slice = createSlice({
  name: 'chatV2',
  initialState,
  reducers: {
    chatInputChanged: (state, action) => {
      state.draftChatMessage = action.payload;
    },
    deleteMessage: (state, action) => {
      state.messages = state.messages.filter(message => message.id !== action.payload.id);
    },
    updateIsInteractiveModeActive: (state, action) => {
      state.isInteractiveModeActive = action.payload;
    },
    savedPromptClicked: state => {
      state.isSavedPrompt = true;
    },
    savedPromptChanged: state => {
      state.isSavedPrompt = false;
    },
    addPromptMessageToThread: (state, action) => {
      state.messages.unshift(createSystemMessage({
        message: action.payload,
        threadId: state.threadId
      }));
    },
    addUserMessageToThread: (state, action) => {
      state.messages.unshift(createUserMessage({
        message: action.payload,
        threadId: state.threadId
      }));
    },
    closeMoveToLastChatBanner: state => {
      state.isCurrentThreadAutocreated = false;
    },
    closeCsatSurvey: state => {
      state.showCsatSurvey = false;
      state.shouldPollCsatSurvey = false;
    },
    setAppSearchScope: (state, action) => {
      state.appSearchScope = action.payload;
    },
    setConnectedApps: (state, action) => {
      const connectedApps = action.payload;
      const previousAppControl = state.appsControl;
      state.appsControl = Object.assign({}, previousAppControl, {
        connectedApps
      });
    },
    startNewChatPending: state => {
      // TODO: This is not used anywhere.
      state.ablyChannelConnectingState = 'connecting';
      state.fetchStatus = 'loading';
      state.isCreatingThread = true;
    },
    startNewChatRejected: state => {
      // TODO: This is not used anywhere.
      state.ablyChannelConnectingState = 'failed';
      state.fetchStatus = 'failed';
      state.isCreatingThread = false;
    },
    startNewChatFulfilled: (state, action) => {
      // TODO: This is not used anywhere.
      state.ablyChannelConnectingState = 'success';
      state.ablyChannelId = action.payload.ablyChannelId;
      state.fetchStatus = 'success';
      state.hasMore = false;
      state.isCurrentThreadAutocreated = false;
      state.isWaitingForResponse = false;
      state.messages = [];
      state.offset = undefined;
      state.pagingStatus = 'success';
      state.threadId = action.payload.id;
      state.draftChatMessage = '';
      state.isCreatingThread = false;
    },
    chatDisconnectPending: state => {
      state.ablyChannelConnectingState = 'connecting';
    },
    chatDisconnectSuccess: state => {
      state.ablyChannelConnectingState = 'success';
      // TODO: Should we clear the ablyChannelId?
    },
    chatResponseStarted: (state, action) => {
      // we do not always have a user message that corresponds to content sent from the BE
      // e.g. confirmation acknowledgement and confirming a tool call creates a separate interactions
      if (action.payload.userMessage) {
        state.streaming.interactionId = action.payload.userMessage.interactionId;
        if (state.threadId === undefined) {
          throw new Error('Missing threadId in chatV2 state.');
        }
        state.messages.map(message => {
          if (message.interactionId === PLACEHOLDER_INTERACTION_ID_USER_MESSAGE) {
            return action.payload.userMessage;
          }
          return message;
        });
        state.messages.unshift(
        // This is required to create a response section in the UI immediately after the user message
        // This is not ideal since it creates a blank message in our state however it does not impact user experience
        createCopilotMessage({
          threadId: state.threadId,
          interactionId: state.streaming.interactionId,
          sections: []
        }));
      }
    },
    chatResponseMessageStarted: (state, action) => {
      // We can expect to have start > delta (0 or more) > end
      // and this loop can continue multiple times before chatResponseEnded.

      if (state.messages.length === 0) {
        throw new Error('No streaming message in state to update.');
      }
      if (state.threadId === undefined) {
        throw new Error('Missing threadId in chatV2 state.');
      }
      state.messages.unshift(createCopilotMessage({
        threadId: state.threadId,
        interactionId: state.streaming.interactionId,
        sections: [action.payload.section]
      }));
      state.streaming.currentToolCall = '';
    },
    chatResponseMessageDelta: (state, action) => {
      const found = state.messages.some(msg => msg.sections.some(sec => {
        if (sec.id === action.payload.id && doesSectionSupportStreaming(sec)) {
          sec.text += action.payload.text;
          return true;
        }
        return false;
      }));
      if (!found) {
        throw new Error(`No message found with LlmResponseSection id ${action.payload.id}.`);
      }
    },
    chatResponseMessageEnded: (state, action) => {
      const messageIndex = state.messages.findIndex(msg => {
        return (msg === null || msg === void 0 ? void 0 : msg.interactionId) === action.payload.message.interactionId;
      });
      if (messageIndex !== -1) {
        state.messages[messageIndex] = action.payload.message;
      } else {
        throw new Error(`No message found with id ${action.payload.message.id}.`);
      }
    },
    chatToolCallStarted: (state, action) => {
      state.streaming.currentToolCall = getCopilotV2ToolStartText(action.payload, state.appsControl.connectedApps, state.appSearchScope);
    },
    chatToolCallEnded: (state, action) => {
      if (state.threadId === undefined) {
        throw new Error('Missing threadId in chatV2 state.');
      }

      // sometimes the `message: null` is sent from the BE
      if (action.payload.message) {
        state.messages.unshift(action.payload.message);
      }
    },
    chatResponseEnded: (state, action) => {
      if (state.threadId === undefined) {
        throw new Error('Missing threadId in chatV2 state.');
      }
      if (state.messages.length === 0) {
        throw new Error('No streaming message in state to update.');
      }
      if (!state.streaming.interactionId) {
        throw new Error('No interaction in state to update.');
      }

      // Remove our empty copilot message that we created in chatResponseStarted
      state.messages = state.messages.filter(message => !message.id.startsWith('copilot-message-'));
      state.interactionDurations[state.streaming.interactionId] = action.payload.duration;
      state.streaming.currentToolCall = '';
      state.streaming.interactionId = '';
      state.isWaitingForResponse = false;
    },
    chatConfirmationResponseAcknowledged: (state, action) => {
      const interactionToUpdateId = action.payload.message.interactionId;

      // find the message that needs to be updated with latest confirmation status
      state.messages = state.messages.map(message => {
        if (message.id === interactionToUpdateId) {
          return Object.assign({}, message, {
            sections: message.sections.map(section => {
              var _action$payload$messa;
              // replace the confirmation section with the latest section from the BE - this will have the updated status
              if (section.id === ((_action$payload$messa = action.payload.message.sections) === null || _action$payload$messa === void 0 || (_action$payload$messa = _action$payload$messa[0]) === null || _action$payload$messa === void 0 ? void 0 : _action$payload$messa.id)) {
                return action.payload.message.sections[0];
              }
              return section;
            })
          });
        }
        return message;
      });
    },
    chatResponseException: (state, action) => {
      if (state.threadId === undefined) {
        throw new Error('Missing threadId in chatV2 state.');
      }
      if (action.payload.message.sections[0] === undefined) {
        throw new Error('Missing section in exception response message.');
      }
      if (action.payload.message.errorCode === 'MODERATION') {
        state.messages = state.messages.filter(message => {
          return (
            // Keep messages that are both:
            // 1. not part of the exception message interaction
            // 2. 'USER messages
            message.interactionId === action.payload.message.interactionId && message.messageType === 'USER'
          );
        });
      }
      state.messages.unshift(createErrorMessage({
        threadId: state.threadId,
        interactionId: state.streaming.interactionId,
        section: action.payload.message.sections[0]
      }));
      state.streaming.currentToolCall = '';
      state.streaming.interactionId = '';
      state.isWaitingForResponse = false;
    },
    chatUserMessageSentPending: (state, action) => {
      if (state.threadId === undefined) {
        throw new Error('Missing threadId in chatV2 state.');
      }
      const {
        message,
        fileData,
        hideUserMessage
      } = action.payload;
      state.streaming.currentToolCall = '';
      state.draftChatMessage = '';
      state.isWaitingForResponse = true;
      if (hideUserMessage) {
        return;
      }

      // TODO: We don't have the interactionId yet. We will need to set it once we get it
      // back from the backend in order to use it to group the user message and copilot
      // messages as a single interaction.
      state.messages.unshift(createUserMessage({
        message,
        threadId: state.threadId,
        interactionId: PLACEHOLDER_INTERACTION_ID_USER_MESSAGE,
        fileData
      }));
    },
    chatUserMessageSentException: (state, action) => {
      const {
        context,
        message,
        template
      } = action.payload;
      state.isWaitingForResponse = false;
      state.failedUserMessages.push({
        messageId: state.messages[0].id,
        message,
        metaInfo: {
          source: 'CHAT',
          template,
          pageContext: context.pageContext
        }
      });
    },
    /**
     * @temporary This action is used for handling exceptions in V1 actions that are not supported in V2.
     * @expiration 2025-10-15
     * @deprecated
     * This happens when a valid V1 action cannot be translated to a V2 tool call
     */
    chatUserMessageActionException: state => {
      if (state.threadId === undefined) {
        throw new Error('Missing threadId in chatV2 state.');
      }
      // Create a new message in streaming state that will be updated with incoming responses.
      state.messages.unshift(createActionExceptionMessage({
        threadId: state.threadId,
        interactionId: state.streaming.interactionId
      }));
      state.streaming.currentToolCall = '';
      state.isWaitingForResponse = false;
    }
  },
  extraReducers: builder => {
    builder.addCase(initializeAblyChannel.pending, state => {
      state.ablyChannelConnectingState = 'connecting';
      state.fetchStatus = 'loading';
      state.isCreatingThread = true;
    });
    builder.addCase(initializeAblyChannel.rejected, state => {
      state.ablyChannelConnectingState = 'failed';
      state.fetchStatus = 'failed';
      state.isCreatingThread = false;
    });
    builder.addCase(initializeAblyChannel.fulfilled, (state, action) => {
      state.ablyChannelConnectingState = 'success';
      state.fetchStatus = 'success';
      state.ablyChannelId = action.payload.thread.ablyChannelId;
      state.isReadOnly = action.payload.thread.isReadOnly || false;
      state.threadId = action.payload.thread.id;
      state.isCurrentThreadAutocreated = false;
      state.messages = [];
      state.isWaitingForResponse = false;
      state.hasMore = false;
      state.offset = undefined;
      state.pagingStatus = 'success';
      state.isCreatingThread = false;
    });
    builder.addCase(copilotV2GetLatestOrCreateThread.pending, state => {
      state.ablyChannelConnectingState = 'connecting';
      state.fetchStatus = 'initializing';
    });
    builder.addCase(copilotV2GetLatestOrCreateThread.rejected, state => {
      state.ablyChannelConnectingState = 'failed';
      state.fetchStatus = 'failed';
    });
    builder.addCase(copilotV2GetLatestOrCreateThread.fulfilled, (state, action) => {
      state.ablyChannelConnectingState = 'success';
      state.fetchStatus = 'success';
      state.ablyChannelId = action.payload.thread.ablyChannelId;
      state.threadId = action.payload.thread.id;
      state.isCurrentThreadAutocreated = action.payload.thread.isAutoCreated;
      state.isReadOnly = action.payload.thread.isReadOnly || false;
      state.messages = action.payload.messages;
      state.isWaitingForResponse = false;
      state.hasMore = action.payload.hasMore;
      state.offset = action.payload.messageOffset || undefined;
      state.pagingStatus = 'idle';
      if (action.meta.arg.initialMessage) {
        state.messages.unshift(createSystemMessage({
          message: action.meta.arg.initialMessage,
          threadId: state.threadId
        }));
      }
    });
    builder.addCase(copilotV2GetThreadWithMessages.pending, state => {
      state.ablyChannelConnectingState = 'connecting';
      state.fetchStatus = 'loading';
    });
    builder.addCase(copilotV2GetThreadWithMessages.rejected, state => {
      state.ablyChannelConnectingState = 'failed';
      state.fetchStatus = 'failed';
    });
    builder.addCase(copilotV2GetThreadWithMessages.fulfilled, (state, action) => {
      state.ablyChannelConnectingState = 'success';
      state.fetchStatus = 'success';
      state.ablyChannelId = action.payload.thread.ablyChannelId;
      state.threadId = action.payload.thread.id;
      state.isCurrentThreadAutocreated = action.payload.thread.isAutoCreated;
      state.isReadOnly = action.payload.thread.isReadOnly || false;
      state.messages = action.payload.messages;
      state.isWaitingForResponse = false;
      state.hasMore = action.payload.hasMore;
      state.offset = action.payload.messageOffset || undefined;
    });
    builder.addCase(copilotV1ThreadMessagesForV2.pending, state => {
      state.ablyChannelConnectingState = 'connecting';
      state.fetchStatus = 'loading';
    });
    builder.addCase(copilotV1ThreadMessagesForV2.rejected, state => {
      state.ablyChannelConnectingState = 'failed';
      state.fetchStatus = 'failed';
    });
    builder.addCase(copilotV1ThreadMessagesForV2.fulfilled, (state, action) => {
      state.messages = action.payload.messages;
      state.hasMore = action.payload.hasMore;
      state.offset = action.payload.messageOffset || undefined;
      state.pagingStatus = 'success';
      state.fetchStatus = 'success';
      state.isWaitingForResponse = false;
      state.isReadOnly = true;
    });
    builder.addCase(copilotV2SoftDeleteAndGetLatestOrCreateNew.fulfilled, (state, action) => {
      state.ablyChannelConnectingState = 'success';
      state.ablyChannelId = action.payload.thread.ablyChannelId;
      state.isWaitingForResponse = false;
      state.pagingStatus = 'success';
      state.fetchStatus = 'success';
      state.threadId = action.payload.thread.id;
      state.isCurrentThreadAutocreated = action.payload.thread.isAutoCreated;
      state.isReadOnly = action.payload.thread.isReadOnly || false;
      state.messages = action.payload.messages;
      state.hasMore = action.payload.hasMore;
      state.offset = action.payload.messageOffset || undefined;
    });
    builder.addCase(getShouldSendCsatSurvey.fulfilled, (state, action) => {
      state.showCsatSurvey = action.payload.shouldSendCsatSurvey;

      // Stop polling for the survey once it's shown
      if (state.showCsatSurvey) {
        state.shouldPollCsatSurvey = false;
      }
    });
    builder.addCase(submitCsatSurvey.fulfilled, state => {
      // The user submitted the survey, so we don't need to show it again
      state.shouldPollCsatSurvey = false;
    });
  }
});
export const chatV2Actions = chatV2Slice.actions;
export default chatV2Slice.reducer;